import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Button, Card, Divider, Form, Input, Layout, Row, Select, Space, Tooltip, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { sessionStore, SESSION_ATTRS, ACTIONS } from '../store/session';
import { APIS, getApi } from '../utils/api-utils';
import { notifyError, notifySuccess } from '../utils/notification';
import { getServiceNowConfig, testServiceNowConnection, updateServiceNowConfig } from './actions';

const { Content } = Layout;
const { Option } = Select;
const { Title } = Typography;

const DEFAULT_INCIDENT_PATH = "/api/now/table/incident";

export const ServiceNowSettingsView = () => {
    const [selectedTenantId, setSelectedTenantId] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [lastUpdated, setLastUpdated] = useState<string | undefined>();
    const [updatedBy, setUpdatedBy] = useState<string | undefined>();
    const [currentUsername, setCurrentUsername] = useState<string>("");
    const [incidentPathEditable, setIncidentPathEditable] = useState(false);
    const [form] = Form.useForm();

    const { state, dispatch } = useContext(sessionStore);
    const tenants: any = state.get(SESSION_ATTRS.TENANTS) || [];
    const loggedUser: any = state.get(SESSION_ATTRS.LOGGED_USER);

    const reloadTenants = useCallback(async () => {
        if (loggedUser && loggedUser.buildingId) {
            try {
                const tenantsData = await getApi(APIS.TENANT_API, `/tenants/${loggedUser.buildingId}`);
                dispatch({
                    type: ACTIONS.TENANTS_LOADED,
                    payload: tenantsData
                });
            } catch (error) {
                console.error('Error reloading tenants:', error);
            }
        }
    }, [loggedUser, dispatch]);

    useEffect(() => {
        reloadTenants();
    }, [reloadTenants]);

    const loadConfig = useCallback(async (tenantId: string) => {
        if (!loggedUser?.buildingId) {
            notifyError('Missing building context');
            return;
        }
        setIsLoading(true);
        try {
            const config = await getServiceNowConfig(loggedUser.buildingId, tenantId);
            form.setFieldsValue({
                instanceUrl: config?.instanceUrl || "",
                incidentPath: config?.incidentPath || DEFAULT_INCIDENT_PATH,
                username: config?.username || "",
                password: ""
            });
            setLastUpdated(config?.updatedAt);
            setUpdatedBy(config?.updatedBy?.name);
            setCurrentUsername(config?.username || "");
            setIncidentPathEditable(false);
        } catch (error) {
            console.error('Error loading ServiceNow config:', error);
            notifyError('Failed to load ServiceNow settings');
        } finally {
            setIsLoading(false);
        }
    }, [form]);

    useEffect(() => {
        if (selectedTenantId) {
            loadConfig(selectedTenantId);
        } else {
            form.resetFields();
            setLastUpdated(undefined);
            setUpdatedBy(undefined);
        }
    }, [selectedTenantId, loadConfig, form]);

    const onTenantChange = (value: string) => {
        setSelectedTenantId(value);
    };

    const onSave = async () => {
        if (!selectedTenantId) return;
        try {
            const values = await form.validateFields();
            if (!values.password && values.username !== currentUsername) {
                notifyError('Password is required to change the username');
                return;
            }
            setIsSaving(true);
            const payload: any = {
                instanceUrl: values.instanceUrl,
                incidentPath: values.incidentPath || DEFAULT_INCIDENT_PATH
            };
            if (values.password) {
                payload.username = values.username;
                payload.password = values.password;
            }
            if (!loggedUser?.buildingId) {
                notifyError('Missing building context');
                return;
            }
            const updated = await updateServiceNowConfig(loggedUser.buildingId, selectedTenantId, payload);
            form.setFieldsValue({
                instanceUrl: updated?.instanceUrl || values.instanceUrl,
                incidentPath: updated?.incidentPath || values.incidentPath || DEFAULT_INCIDENT_PATH,
                username: updated?.username || values.username,
                password: ""
            });
            setLastUpdated(updated?.updatedAt);
            setUpdatedBy(updated?.updatedBy?.name);
            setCurrentUsername(updated?.username || values.username);
            setIncidentPathEditable(false);
            notifySuccess('ServiceNow settings updated');
        } catch (error) {
            console.error('Error saving ServiceNow config:', error);
            notifyError('Failed to update ServiceNow settings');
        } finally {
            setIsSaving(false);
        }
    };

    const onTestConnection = async () => {
        try {
            const values = await form.validateFields();
            const username = values.username?.trim();
            const password = values.password?.trim();
            if (!username || !password) {
                notifyError('Please enter the ServiceNow username and password to test the connection');
                return;
            }
            if (!loggedUser?.buildingId) {
                notifyError('Missing building context');
                return;
            }
            setIsTesting(true);
            await testServiceNowConnection({
                buildingId: loggedUser.buildingId,
                instanceUrl: values.instanceUrl,
                incidentPath: values.incidentPath || DEFAULT_INCIDENT_PATH,
                username,
                password
            });
            notifySuccess('ServiceNow connection successful');
        } catch (error: any) {
            if (error?.errorFields) {
                notifyError('Please complete the required fields to test the connection');
                return;
            }
            const message = error?.response?.data?.message || error?.message || 'ServiceNow connection test failed';
            notifyError(message);
        } finally {
            setIsTesting(false);
        }
    };

    return (
        <Space direction='vertical' style={{ width: '100%' }}>
            <Space direction='vertical' style={{ width: '100%' }}>
                <Row style={{ marginTop: 15, display: 'flex', flexDirection: 'column' }}>
                    <Title level={4} style={{ marginBottom: 4 }}>ServiceNow Settings</Title>
                </Row>
                <Row className="grid-options">
                    <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
                        <Select
                            value={selectedTenantId || undefined}
                            onChange={onTenantChange}
                            style={{ width: 260 }}
                            placeholder="Select Tenant"
                        >
                            {tenants.map((tenant: any) => (
                                <Option key={tenant.tenantId} value={tenant.tenantId}>{tenant.name}</Option>
                            ))}
                        </Select>
                    </Space>
                </Row>
            </Space>
            <Content className="table-wrapper">
                {selectedTenantId ? (
                    <Card
                        title="Connection Details"
                        bordered={false}
                        style={{ maxWidth: 640 }}
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            disabled={isLoading}
                        >
                        <Form.Item
                            name="instanceUrl"
                            label="ServiceNow Instance URL"
                            rules={[{ required: true, message: 'Please enter the instance URL' }]}
                            extra="example: https://yourinstance.service-now.com"
                        >
                            <Input placeholder="https://yourinstance.service-now.com" />
                        </Form.Item>
                            <Form.Item
                                label="ServiceNow Incident Path"
                                extra="Default: /api/now/table/incident"
                            >
                                <Input.Group compact style={{ display: 'flex' }}>
                                    <Form.Item
                                        name="incidentPath"
                                        rules={[{ required: true, message: 'Please enter the incident path' }]}
                                        style={{ flex: 1, marginBottom: 0 }}
                                    >
                                        <Input
                                            placeholder={DEFAULT_INCIDENT_PATH}
                                            disabled={!incidentPathEditable}
                                        />
                                    </Form.Item>
                                    <Tooltip title={incidentPathEditable ? "Editing enabled" : "Edit"}>
                                        <Button
                                            icon={<EditOutlined />}
                                            onClick={() => setIncidentPathEditable(true)}
                                            disabled={incidentPathEditable}
                                            style={{ marginLeft: 8, height: 40 }}
                                        >
                                            Edit
                                        </Button>
                                    </Tooltip>
                                </Input.Group>
                            </Form.Item>
                            <Divider style={{ margin: '12px 0' }} />
                        <Form.Item
                            name="username"
                            label="ServiceNow Username"
                            rules={[{ required: true, message: 'Please enter the ServiceNow username' }]}
                        >
                            <Input.Password
                                placeholder="username"
                                visibilityToggle
                                autoComplete="off"
                            />
                        </Form.Item>
                            <Form.Item
                                name="password"
                                label="ServiceNow Password"
                                tooltip="Leave empty to keep the current password"
                                extra="Leave blank to keep existing credentials."
                            >
                                <Input.Password placeholder="password" />
                            </Form.Item>
                            <Form.Item style={{ marginTop: 8 }}>
                                <Space>
                                    <Button
                                        type="primary"
                                        className="btn btn--primary"
                                        onClick={onSave}
                                        loading={isSaving}
                                        disabled={isLoading || isTesting}
                                    >
                                        Save Settings
                                    </Button>
                                    <Button
                                        className="btn btn--o-primary"
                                        onClick={onTestConnection}
                                        loading={isTesting}
                                        disabled={isLoading || isSaving}
                                    >
                                        Test Connection
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>
                ) : (
                    <Alert
                        type="info"
                        showIcon
                        message="Select a tenant to configure ServiceNow settings."
                    />
                )}
            </Content>
        </Space>
    );
};
